import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useCustomerId } from "../../hooks/useCustomerId";
import { getByCustomer, getLatestRun } from "../../api/projects";
import { ProjectTile } from "../../features/projects/domain/types";
import { toSigFigs as formatSigFigs, formatCurrencyFull, formatScientific, formatCompactValue } from "../../utils/format";
import { useAllBaselines } from "../../features/baseline/hooks/useBaseline";
import type { BaselineSummary } from "../../api/baseline";
import {
    Card,
    CardHeader,
    Button,
    PageLayout,
    PageHeader,
    Section,
    PageSpinner,
    Badge,
} from "../../components";

type SortMetric = "usd" | "dalys" | "pdfs" | "usd_per_mw";

interface ProjectComparison {
    project: ProjectTile;
    run: any | null;
    totals: {
        dalys: number;
        pdfs: number;
        usd: number;
    } | null;
    capacityMw: number;
    generationMwh: number;
}

export default function CompareProjects() {
    const { token } = useAuth();
    const customerId = useCustomerId(token);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<SortMetric>("usd");
    const [phase, setPhase] = useState<"select" | "compare">("select");

    // Fetch baselines for grid context
    const { data: baselines } = useAllBaselines();
    const baselineMap = useMemo(() => {
        const map: Record<string, BaselineSummary> = {};
        baselines?.forEach(b => { map[b.ba_code] = b; });
        return map;
    }, [baselines]);

    // Fetch all projects
    const { data: projects = [], isLoading: projectsLoading } = useQuery<ProjectTile[]>({
        queryKey: ["compare-projects", customerId],
        queryFn: async () => {
            if (!customerId || !token) return [];
            const data = await getByCustomer(customerId, token);
            const arr = Array.isArray(data) ? data : data.projects || [];
            return arr.map((proj: any) => ({
                project_id: proj.project_id,
                name: proj.project_name ?? `Project ${proj.project_id}`,
                status: proj.existing_project_flag ? "Operating" : "Prospective",
                tech_type: proj.tech_type_id || proj.tech_type,
                mw_capacity: proj.nameplate_capacity || proj.mw_capacity || 0,
                eia_ba_code: proj.eia_ba_code,
            }));
        },
        staleTime: 1000 * 60 * 5,
        enabled: !!customerId && !!token,
    });

    // Fetch latest runs for selected projects
    const selectedProjectIds = Array.from(selectedIds);
    const runQueries = useQuery({
        queryKey: ["compare-runs", ...selectedProjectIds],
        queryFn: async () => {
            if (!token) return {};
            const results: Record<string, any> = {};
            await Promise.all(
                selectedProjectIds.map(async (id) => {
                    try {
                        results[id] = await getLatestRun(id, token);
                    } catch {
                        results[id] = null;
                    }
                })
            );
            return results;
        },
        enabled: phase === "compare" && selectedProjectIds.length >= 2 && !!token,
        staleTime: 1000 * 60 * 5,
    });

    const comparisons: ProjectComparison[] = useMemo(() => {
        if (phase !== "compare" || !runQueries.data) return [];

        return selectedProjectIds
            .map((id) => {
                const project = projects.find((p) => p.project_id === id);
                if (!project) return null;

                const run = runQueries.data[id];
                let totals: ProjectComparison["totals"] = null;

                // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure)
                if (run?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) {
                    const t = run.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
                    totals = {
                        dalys: t.DALYs || 0,
                        pdfs: t.PDFs || 0,
                        usd: t.USD || 0,
                    };
                }

                // Extract generation MWh
                const genGwh = run?.summary_json?.generation_metadata?.actual_generation_gwh;
                const generationMwh = genGwh ? genGwh * 1000 : 0;

                return {
                    project,
                    run,
                    totals,
                    capacityMw: project.mw_capacity || 0,
                    generationMwh,
                } as ProjectComparison;
            })
            .filter(Boolean) as ProjectComparison[];
    }, [phase, runQueries.data, selectedProjectIds, projects]);

    // Sort comparisons
    const sortedComparisons = useMemo(() => {
        return [...comparisons].sort((a, b) => {
            if (!a.totals || !b.totals) return 0;
            switch (sortBy) {
                case "usd":
                    return a.totals.usd - b.totals.usd;
                case "dalys":
                    return a.totals.dalys - b.totals.dalys;
                case "pdfs":
                    return a.totals.pdfs - b.totals.pdfs;
                case "usd_per_mw":
                    return (
                        (a.capacityMw ? a.totals.usd / a.capacityMw : 0) -
                        (b.capacityMw ? b.totals.usd / b.capacityMw : 0)
                    );
                default:
                    return 0;
            }
        });
    }, [comparisons, sortBy]);

    const toggleProject = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else if (next.size < 4) {
                next.add(id);
            }
            return next;
        });
    };

    if (projectsLoading) {
        return <PageSpinner message="Loading projects..." />;
    }

    return (
        <PageLayout>
            <PageHeader
                title="Compare Projects"
                subtitle="Select 2-4 projects to compare impact metrics side by side"
                actions={
                    <Link to="/app/projects">
                        <Button variant="ghost">Back to Projects</Button>
                    </Link>
                }
            />

            {phase === "select" && (
                <Section>
                    <Card>
                        <CardHeader>
                            Select Projects ({selectedIds.size}/4)
                        </CardHeader>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {projects.map((p) => (
                                <label
                                    key={p.project_id}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                                        ${selectedIds.has(p.project_id) ? "bg-quantum-500/10 border border-quantum-500/30" : "hover:bg-surface-600 border border-transparent"}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(p.project_id)}
                                        onChange={() => toggleProject(p.project_id)}
                                        disabled={!selectedIds.has(p.project_id) && selectedIds.size >= 4}
                                        className="h-4 w-4 accent-quantum-500"
                                    />
                                    <div className="flex-1">
                                        <span className="text-body-sm font-medium text-text-primary">
                                            {p.name}
                                        </span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-caption text-text-muted">
                                                {p.mw_capacity} MW
                                            </span>
                                            <Badge variant="neutral" size="sm">{p.status}</Badge>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-border">
                            <Button
                                variant="primary"
                                onClick={() => setPhase("compare")}
                                disabled={selectedIds.size < 2}
                            >
                                Compare {selectedIds.size} Projects
                            </Button>
                        </div>
                    </Card>
                </Section>
            )}

            {phase === "compare" && (
                <>
                    <div className="flex items-center gap-4 mb-6">
                        <Button variant="ghost" onClick={() => setPhase("select")}>
                            Change Selection
                        </Button>
                        <div className="flex items-center gap-2">
                            <span className="text-caption text-text-muted">Sort by:</span>
                            {(
                                [
                                    ["usd", "Economic (USD)"],
                                    ["dalys", "Health (DALYs)"],
                                    ["pdfs", "Ecosystem (PDFs)"],
                                    ["usd_per_mw", "USD/MW"],
                                ] as [SortMetric, string][]
                            ).map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setSortBy(key)}
                                    className={`px-3 py-1.5 rounded-md text-caption transition-colors
                                        ${sortBy === key
                                            ? "bg-quantum-500/20 text-quantum-400"
                                            : "text-text-muted hover:text-text-secondary"
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {runQueries.isLoading ? (
                        <PageSpinner message="Loading project results..." />
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {sortedComparisons.map(({ project, totals, capacityMw, generationMwh }) => {
                                const baseline = project.eia_ba_code ? baselineMap[project.eia_ba_code] : undefined;
                                const gridGenMwh = baseline?.total_generation_mwh;
                                return (
                                <Card key={project.project_id}>
                                    <div className="p-5 space-y-4">
                                        <div>
                                            <Link
                                                to={`/app/projects/${encodeURIComponent(project.project_id)}?name=${encodeURIComponent(project.name)}`}
                                                className="text-body-sm font-semibold text-text-primary hover:text-quantum-400 transition-colors"
                                            >
                                                {project.name}
                                            </Link>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-caption text-text-muted">
                                                    {capacityMw} MW
                                                </span>
                                                <Badge variant="neutral" size="sm">{project.status}</Badge>
                                            </div>
                                        </div>

                                        {totals ? (
                                            <div className="space-y-3">
                                                <div>
                                                    <CompareMetric
                                                        label="Economic Impact"
                                                        value={formatCurrency(totals.usd)}
                                                        color="text-economic-400"
                                                    />
                                                    {generationMwh > 0 && (
                                                        <p className="text-[10px] text-text-muted">{formatScientific(Math.abs(totals.usd / generationMwh))} /MWh</p>
                                                    )}
                                                    {baseline?.resource_scarcity_usd_per_mwh != null && gridGenMwh && (
                                                        <p className="text-[10px] text-red-400/60">Grid: {formatCurrency(Math.abs(baseline.resource_scarcity_usd_per_mwh * gridGenMwh))}/yr</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <CompareMetric
                                                        label="Human Health"
                                                        value={`${formatSigFigs(totals.dalys)} DALYs`}
                                                        color="text-health-400"
                                                    />
                                                    {generationMwh > 0 && (
                                                        <p className="text-[10px] text-text-muted">{formatScientific(Math.abs(totals.dalys / generationMwh))} /MWh</p>
                                                    )}
                                                    {baseline?.human_health_dalys_per_mwh != null && gridGenMwh && (
                                                        <p className="text-[10px] text-red-400/60">Grid: {formatCompactValue(Math.abs(baseline.human_health_dalys_per_mwh * gridGenMwh))} DALYs/yr</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <CompareMetric
                                                        label="Ecosystem"
                                                        value={`${formatSigFigs(totals.pdfs)} PDFs`}
                                                        color="text-ecosystem-400"
                                                    />
                                                    {generationMwh > 0 && (
                                                        <p className="text-[10px] text-text-muted">{formatScientific(Math.abs(totals.pdfs / generationMwh))} /MWh</p>
                                                    )}
                                                    {baseline?.ecosystem_quality_species_yr_per_mwh != null && gridGenMwh && (
                                                        <p className="text-[10px] text-red-400/60">Grid: {formatCompactValue(Math.abs(baseline.ecosystem_quality_species_yr_per_mwh * gridGenMwh))} PDFs/yr</p>
                                                    )}
                                                </div>
                                                {capacityMw > 0 && (
                                                    <CompareMetric
                                                        label="Impact per MW"
                                                        value={formatCurrency(totals.usd / capacityMw) + "/MW"}
                                                        color="text-quantum-400"
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-body-sm text-text-muted italic">
                                                No results available
                                            </p>
                                        )}
                                    </div>
                                </Card>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </PageLayout>
    );
}

function CompareMetric({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color: string;
}) {
    return (
        <div>
            <p className="text-caption text-text-muted">{label}</p>
            <p className={`text-body-sm font-semibold ${color}`}>{value}</p>
        </div>
    );
}

// Use formatCurrencyFull from utils/format
const formatCurrency = formatCurrencyFull;
